import { Link } from "react-router-dom";
import { ArrowRight, Code2, Globe, Blocks, Cloud, Workflow, PlugZap, BarChart3, Sparkles } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useSite } from "@/lib/content";

const ICONS = { Code2, Globe, Blocks, Cloud, Workflow, PlugZap, BarChart3, Sparkles };
export const iconFor = (name) => ICONS[name] || Code2;

export default function WhatWeBuild() {
  const { data } = useSite();
  const services = data?.services || [];

  return (
    <section className="relative bg-void py-28 sm:py-36" data-testid="what-we-build">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <Eyebrow>What We Build</Eyebrow>
          <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <h2 className="max-w-xl font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Software capabilities, engineered as one connected system.
            </h2>
            <Link
              to="/services"
              data-testid="what-we-build-view-all"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-blue-400 transition-colors hover:text-blue-300"
            >
              View all services
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => {
            const Icon = iconFor(s.icon);
            const content = (
              <div className="group relative h-full overflow-hidden rounded-2xl border border-slate-800/80 bg-navy/70 p-7 transition-[border-color,transform,background-color] duration-500 hover:-translate-y-1 hover:border-blue-500/40">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/[0.07] to-indigo-500/[0.02] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-500/25 bg-blue-500/10 text-blue-400">
                      <Icon size={20} />
                    </span>
                    {s.expanding && (
                      <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                        Expanding
                      </span>
                    )}
                  </div>
                  <h3 className="mt-6 font-display text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.tagline}</p>
                </div>
              </div>
            );
            return (
              <Reveal key={s.id || s.slug} delay={i * 0.06} y={20}>
                {s.status === "published" ? (
                  <Link to={`/services/${s.slug}`} data-testid={`service-card-${s.slug}`} className="block h-full">
                    {content}
                  </Link>
                ) : (
                  <div data-testid={`service-card-${s.slug}`} className="h-full">{content}</div>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
